import type { AppLanguage } from "@/lib/language";

export type PriceCurrency = "USD" | "BRL";
export type BillingPeriod = "monthly" | "annual";

/** Monthly prices per plan (full amount). */
const MONTHLY_PRICES: Record<PriceCurrency, Record<string, number>> = {
  USD: { free: 0, starter: 12, pro: 24, business: 120 },
  BRL: { free: 0, starter: 60, pro: 120, business: 600 },
};

/**
 * Annual plans: 20% discount shown as monthly-equivalent price.
 * (monthly × 12 × 0.8 / 12 = monthly × 0.8)
 */
const ANNUAL_PRICES_MONTHLY_EQUIV: Record<PriceCurrency, Record<string, number>> = {
  USD: { free: 0, starter: 9.6, pro: 19.2, business: 96 },
  BRL: { free: 0, starter: 48, pro: 96, business: 480 },
};

/** Total billed once per year (monthly × 12 × 0.8). */
export const ANNUAL_PRICES_TOTAL: Record<PriceCurrency, Record<string, number>> = {
  USD: { free: 0, starter: 115.20, pro: 230.40, business: 1152 },
  BRL: { free: 0, starter: 576, pro: 1152, business: 5760 },
};

export function getCurrency(language: AppLanguage): PriceCurrency {
  return language === "pt" ? "BRL" : "USD";
}

export function formatPrice(planId: string, language: AppLanguage, billing: BillingPeriod = "monthly"): string {
  const currency = getCurrency(language);
  const prices = billing === "annual" ? ANNUAL_PRICES_MONTHLY_EQUIV : MONTHLY_PRICES;
  const amount = prices[currency][planId] ?? 0;
  if (amount === 0) {
    return currency === "BRL" ? "R$0" : "$0";
  }
  if (currency === "BRL") {
    return `R$${Number.isInteger(amount) ? amount : amount.toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  }
  return `$${Number.isInteger(amount) ? amount : amount.toFixed(2)}`;
}

export function formatPeriod(planId: string, language: AppLanguage, billing: BillingPeriod = "monthly"): string {
  if (planId === "free") {
    return language === "pt" ? "para sempre" : "forever";
  }
  return language === "pt" ? "/mês" : "/mo";
}

/** Monthly original price formatted (no discount). */
export function formatMonthlyOriginal(planId: string, language: AppLanguage): string {
  const currency = getCurrency(language);
  const amount = MONTHLY_PRICES[currency][planId] ?? 0;
  if (amount === 0) return "";
  if (currency === "BRL") return `R$${amount}`;
  return `$${amount}`;
}

/** Amount saved per year on annual billing. */
export function formatAnnualSavings(planId: string, language: AppLanguage): string {
  if (planId === "free") return "";
  const currency = getCurrency(language);
  const monthly = MONTHLY_PRICES[currency][planId] ?? 0;
  const annual = ANNUAL_PRICES_TOTAL[currency][planId] ?? 0;
  const saved = monthly * 12 - annual;
  if (saved <= 0) return "";
  if (currency === "BRL") {
    return `R$${Number.isInteger(saved) ? saved : saved.toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  }
  return `$${Number.isInteger(saved) ? saved : saved.toFixed(2)}`;
}

/** Annual total formatted as a string, e.g. "$230.40/yr" */
export function formatAnnualTotal(planId: string, language: AppLanguage): string {
  if (planId === "free") return "";
  const currency = getCurrency(language);
  const amount = ANNUAL_PRICES_TOTAL[currency][planId] ?? 0;
  const label = language === "pt" ? "/ano" : "/yr";
  if (currency === "BRL") {
    return `R$${Number.isInteger(amount) ? amount : amount.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${label}`;
  }
  return `$${Number.isInteger(amount) ? amount : amount.toFixed(2)}${label}`;
}
